import { DeleteOutline, EditOutlined } from '@mui/icons-material';
import { Box, Divider, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import { RowActionsMenu } from 'src/Common/Components/RowActionsMenu';
import { ServiceIntegrationModel, ServiceState } from '@ekkogmbh/apisdk';
import useDeviceIntegrations from '../Providers/DeviceIntegrationProvider';
import { getManufacturer, getServiceKnowledge } from '../Helper/integrationKnowledge';
import { ServiceAvatar } from './ServiceAvatar';

interface Props {
  integrationIndex: number;
  onDelete: () => void;
  onClick: () => void;
  onEdit: () => void;
}

/** A single configured-integration card in the gallery. */
export const IntegrationCard = ({ integrationIndex, onDelete, onClick, onEdit }: Props): React.JSX.Element => {
  const { integrations, selectIntegration } = useDeviceIntegrations();
  const integration = integrations[integrationIndex];
  const { serviceName, coordinate, serviceData } = integration;

  // @TODO state schema is not finalized
  const isOffline = serviceData?.state === ServiceState.OFFLINE;
  const offlineMessage = isOffline ? serviceData?.stateMessage : undefined;
  const isShadowed = integration.metadata?.shadowed === true;
  const isEnv = integration.metadata?.env === true;

  const knowledge = getServiceKnowledge(serviceName);
  const manufacturer = knowledge?.manufacturerId ? getManufacturer(knowledge.manufacturerId) : undefined;

  const title = knowledge?.displayName ?? serviceName;
  const subtitle = manufacturer?.name;

  return (
    <Paper
      variant={'outlined'}
      onClick={() => {
        selectIntegration(integrationIndex);
        onClick();
      }}
      sx={{
        p: 2,
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        cursor: 'pointer',
        transition: 'border-color 120ms, box-shadow 120ms',
        '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
      }}
    >
      {/* Header */}
      <Stack direction={'row'} alignItems={'center'} spacing={1.5}>
        <ServiceAvatar serviceName={serviceName} />
        <Stack sx={{ minWidth: 0, flexGrow: 1 }} spacing={0.25}>
          <Stack direction={'row'} alignItems={'center'} spacing={0.75} sx={{ minWidth: 0 }}>
            <Typography fontWeight={700} noWrap title={title} color={isShadowed ? 'purple' : undefined}>
              {title}
            </Typography>
          </Stack>
          {subtitle && (
            <Typography variant={'caption'} color={'text.secondary'} noWrap>
              {subtitle}
            </Typography>
          )}
        </Stack>
        {!isEnv && (
          <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
            <RowActionsMenu<ServiceIntegrationModel>
              row={integration}
              actions={[
                // { label: 'Details', icon: <InfoOutlined fontSize={'small'} />, onClick: onSelect },
                {
                  label: 'Edit',
                  icon: <EditOutlined fontSize={'small'} />,
                  onClick: () => {
                    selectIntegration(integrationIndex);
                    onEdit();
                  },
                },
                {
                  label: 'Delete',
                  icon: <DeleteOutline fontSize={'small'} />,
                  onClick: () => {
                    selectIntegration(integrationIndex);
                    onDelete();
                  },
                },
              ]}
            />
          </Box>
        )}
      </Stack>

      <Divider />

      {/* Status + coordinate */}
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        <Typography variant={'body2'} noWrap sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
          {coordinate}
        </Typography>
        {offlineMessage && (
          <Typography variant={'caption'} color={'error'} noWrap>
            {offlineMessage}
          </Typography>
        )}
      </Stack>

      {/**
       * Capabilities are commented out, because adapting the mocked types is in my opinion wasted effort.
       *
       * The capabilities, as provided by the device integration system, are just implementation detail that should not be made visible by default.
       * Only in rare cases, for very specific implementations, maybe in the furute?, we could display something.
       *
       * If we do display capabilities explicitly, it should be done in the linking UI, where the actual device type is being referenced
       * and it might be of interest how i.E. "button events" work on the device I'm about to use.
       * However even then, to an end-user this information will rarely matter, because if buttons are a capability of the device, we will
       * simply expose the functionality and not explain that the service happens to implement the capability.
       */}
      {/* (capabilities ?? []).length > 0 && <CapabilityChips capabilities={capabilities} /> */}

      {/**
       * Device counts are commented out, because we have no service data schema yet
       * @TODO write a statistics component that works on any numbers, rather than some device status structs
       */}
      {/* Footer: device counts, pinned to the bottom */}
      {/* <Box sx={{ flexGrow: 1 }} /> */}
      {/* <DeviceCounts status={status} systemName={resolved?.system.name} /> */}
    </Paper>
  );
};
